import React from "react";
import { useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import AppUtils from "@src/utils/appUtils";
import SignupForm from "@src/components/forms/SignupForm";
import { useNavigate } from "react-router-dom";
import {
  signolImagesRepositoryURL,
  OperatorCodeAliasSettings,
  UsernameTypes,
  DEFAULT_INDUSTRY,
} from "@src/constants/constants";
import { ImageWithRetry } from "../../components/atoms";
import ErrorPage from "../../components/layout/ErrorPage";
import { useAuthContext } from "@src/context/authContext";
import { authEndpoints } from "@src/services/endpoints";

const SignupPage = () => {
  const { search } = useLocation();
  const [error, setError] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [prefillValues, setPrefillValues] = React.useState(null);
  const [industry, setIndustry] = React.useState(DEFAULT_INDUSTRY);
  const [usernameType, setUsernameType] = React.useState(
    UsernameTypes.USERNAME,
  );
  const [customerCode, setCustomerCode] = React.useState(null);
  const [employeeNumberSettings, setEmployeeNumberSettings] = React.useState(
    OperatorCodeAliasSettings.aviation,
  );
  const [isSettingUp, setIsSettingUp] = React.useState(false);
  const navigate = useNavigate();
  const { setUserTokens } = useAuthContext();

  const onSignupSubmit = React.useCallback(
    async ({ username, password }) => {
      setIsSettingUp(true);
      await new Promise((resolve) => setTimeout(resolve, 5000));
      try {
        const tokens = await authEndpoints.login({ username, password });
        setUserTokens(tokens);
        window.location.href = "/dashboard";
      } catch {
        navigate(
          usernameType === UsernameTypes.USERNAME
            ? "/loginUsername"
            : "/loginEmail",
        );
      }
    },
    [setUserTokens, navigate, usernameType],
  );

  const signupToken = React.useMemo(() => {
    const query = new URLSearchParams(search);
    return query.get("t");
  }, [search]);

  React.useEffect(() => {
    const query = new URLSearchParams(search);

    const signupToken = query.get("t");
    if (!signupToken) {
      console.error("No valid signupToken", { signupToken });
      setError("Invalid or Expired sign-up link.");
      return;
    }

    try {
      const payload = signupToken.replace("SG-", ".").split(".")[1];
      const params = JSON.parse(AppUtils.base64decode(payload));
      let {
        //exp,
        operatorCodeLabel = "",
        operatorCode = "",
        operatorFirstname = "",
        operatorLastname = "",
        //operatorEmail = '',
        operatorUsername = "",
        // requiredEmail = false, useless field - isn't supplied by generic signup link
      } = params; // query params should be hidden inside encoded token
      // TODO : activation date and expiry date if we need them in future.

      setIndustry(params.industry);
      setCustomerCode(params.customerCode);
      setUsernameType(params.usernameType);

      if (params.operatorCodeLabel) {
        const customerSettings = JSON.parse(operatorCodeLabel);
        setEmployeeNumberSettings(customerSettings);
      } else if (
        params.industry === "shipping" ||
        usernameType === UsernameTypes.USERNAME
      ) {
        setEmployeeNumberSettings(OperatorCodeAliasSettings.shipping);
      }
      if (params.customerCode === "NYK") {
        /* NYK at launch had emails with incorrect operator codes with dashes */
        operatorUsername = operatorUsername.replace(/-|\s/g, "");
      }

      setPrefillValues({
        firstname: operatorFirstname || "", // null values still override defaults, so scrub those here
        lastname: operatorLastname || "",
        username: operatorUsername || "",
        operatorCode,
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search]);

  if (error) {
    return <ErrorPage pagename="signup" />;
  }

  return (
    <div className="bg-white m-auto w-auto rounded-2xl max-w-[800px] shadow-md overflow-clip">
      {isSettingUp ? (
        <>
          <div className="pt-3 mx-auto text-2xl md:text-3xl font-bold text-success text-success-500 text-center leading-snug">
            Success.
          </div>
          <div className="p-6">
            <div className="text-black">
              Your Signol account has been set up. You will be redirected
              shortly.
            </div>
          </div>
        </>
      ) : (
        !loading && (
          <>
            <SignupForm
              onSignupSubmit={onSignupSubmit}
              defaultValues={prefillValues}
              industry={industry}
              usernameType={usernameType}
              customerCode={customerCode}
              employeeNumberSettings={employeeNumberSettings}
              signupToken={signupToken}
            />
            <div className="flex justify-between bg-primary-300 text-white">
              <div className="hidden md:flex mx-8 items-center justify-center">
                <ImageWithRetry
                  src={`${signolImagesRepositoryURL}/SignolLogoWhite.png`}
                  alt="Signol logo"
                  width="60"
                />
              </div>
              <div className="flex flex-1 justify-end gap-2">
                <a
                  onClick={() => {
                    switch (industry) {
                      case "aviation": {
                        navigate("/loginEmail");
                        break;
                      }
                      case "shipping": {
                        navigate("/loginUsername");
                        break;
                      }
                      default: {
                        navigate("/");
                        break;
                      }
                    }
                  }}
                  className="my-4  font-bold underline cursor-pointer"
                >
                  Already have an account?
                </a>
                <a
                  href="mailto:support@signol.io?Subject=Support"
                  className="my-4 mx-8 font-bold underline cursor-pointer"
                >
                  Support
                </a>
              </div>
            </div>
          </>
        )
      )}
    </div>
  );
};

export const signupPageControls = {};

SignupPage.propTypes = AppUtils.getPropTypeFromControls(signupPageControls);

export default SignupPage;
